import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FormGroup,
  FormControlLabel,
  Switch,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { autoCompleteDataFetcher } from '../../services/GenericService/GenericService';
import RemoteDataAutoComplete from '../AutoComplete/CustomAutoComplete/RemoteDataAutoComplete';
import FixedOptionsAutoComplete from '../AutoComplete/CustomAutoComplete/FixedOptionsAutoComplete';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import useAuth from '../../hooks/useAuth';
import { ClientAutoComplete, LocationAutoComplete, PetrohubClientAutoComplete, ProjectAutoComplete } from '../AutoComplete';
import StatusSelect from '../Select/StatusSelect/StatusSelect';
import { equalsIgnoringCase } from '../../utils/stringUtils';
import ReportAddressAutoComplate from '../AutoComplete/ReportAddressAutoComplate/ReportAddressAutoComplate';
import ScenarioAutocomplate from '../AutoComplete/ScenarioAutocomplate/ScenarioAutocomplate';
import ClientApi from '../../services/api/ClientApi';
import { getUserCurrentPageName } from '../../services/AuthService/AuthService';
import { actionMap, PageName } from '../../utils/screenPermission';
import { useDispatch } from 'react-redux';

const getExtraFilter = (ExtraFilterKeys, fields) => {
  const _extraFilter = {};
  if (ExtraFilterKeys?.length) {
    ExtraFilterKeys?.forEach(({ PropName, ValuePath, FilterKey }) => {
      if (!PropName) {
        return;
      }

      const fieldPropObj = fields[PropName];
      let fieldValue = fieldPropObj;
      if (ValuePath) {
        if (fieldPropObj) {
          if (Array.isArray(fieldPropObj)) {
            fieldValue = fieldPropObj.map((item) => item[ValuePath]).filter((val) => val != null);
          } else {
            fieldValue = fieldPropObj[ValuePath];
          }
        } else {
          fieldValue = null;
        }
      }
      const extraFilterKey = FilterKey ?? PropName;
      _extraFilter[extraFilterKey] = fieldValue;
    });
  }
  return _extraFilter;
};

const ALLOWED_LOCATION_FORMATS = new Set([
  '## - ## - ### - ##- W#M',
  'X - ## - X / ##-X-XX',
  '1## / X - ## - X / ##-X-XX / X',
  '1## /  ## - ## - ### - ## - W#M',
]);

const TextFieldElement = ({ config, fields, dispatchValues, setValidationError }) => {
  const { i18nLabel, Text, Field, Required } = config || {};

  useEffect(() => {
    if (Required && setValidationError) {
      if (!fields[Field]) {
        setValidationError(true);
      } else {
        setValidationError(false);
      }
    }
  }, [Field, Required, fields, setValidationError]);
  return (
    <TextField
      fullWidth
      label={i18nLabel || Text}
      name={Field}
      size="small"
      required={Required}
      value={fields[Field]}
      onChange={(e) => dispatchValues(Field, e.target.value)}
    />
  );
};
const MaskTextFieldElement = ({ config, fields, dispatchValues, setValidationError }) => {
  const { i18nLabel, Text, Field, DependentOn } = config || {};
  const format = fields[DependentOn]?.locationFormatter || '';
  const inputRef = useRef(null);
  const previousFormatRef = useRef(format);
  const pendingCursorRef = useRef(null);

  const getMaskPlaceholderType = useCallback((char) => {
    if (char === '9' || char === '#') {
      return 'digit';
    }
    if (char === 'A') {
      return 'upper';
    }
    if (char === 'a') {
      return 'lower';
    }
    if (char === 'X' || char === 'x') {
      return 'alpha';
    }
    return null;
  }, []);

  const isMatchingMaskType = useCallback((char, type) => {
    if (type === 'digit') {
      return /\d/.test(char);
    }
    if (type === 'upper' || type === 'lower' || type === 'alpha') {
      return /[A-Za-z]/.test(char);
    }
    return false;
  }, []);

  const transformByMaskType = useCallback((char, type) => {
    if (type === 'upper') {
      return char.toUpperCase();
    }
    if (type === 'lower') {
      return char.toLowerCase();
    }
    if (type === 'alpha') {
      return char.toUpperCase();
    }
    return char;
  }, []);

  const getFormatRegex = useCallback((maskFormat) => {
    if (!maskFormat) {
      return null;
    }

    const escaped = maskFormat
      .split('')
      .map((char) => {
        const type = getMaskPlaceholderType(char);
        if (type === 'digit') {
          return '\\d';
        }
        if (type === 'upper') {
          return '[A-Z]';
        }
        if (type === 'lower') {
          return '[a-z]';
        }
        if (type === 'alpha') {
          return '[A-Za-z]';
        }
        return char.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      })
      .join('');

    return new RegExp(`^${escaped}$`);
  }, [getMaskPlaceholderType]);

  const isLocationIdentifierValid = useCallback((value, maskFormat, fallbackRegex) => {
    if (!value) {
      return true;
    }

    if (!ALLOWED_LOCATION_FORMATS.has(maskFormat)) {
      return true;
    }

    return fallbackRegex ? fallbackRegex.test(value) : true;
  }, []);

  const buildDisplayValue = useCallback((cleanValue, maskFormat) => {
    if (typeof cleanValue === 'object' && cleanValue !== null && typeof cleanValue.masked === 'string') {
      return cleanValue.masked;
    }

    if (!maskFormat) {
      return cleanValue;
    }

    // Extract clean value if it's an object
    const actualCleanValue = typeof cleanValue === 'object' && cleanValue !== null ? cleanValue.clean : cleanValue;

    // cleanValue is a string of alphanumeric characters
    const candidates = (typeof actualCleanValue === 'string' ? actualCleanValue : '').split('');

    let displayValue = '';
    let candidateIndex = 0;
    for (let i = 0; i < maskFormat.length; i += 1) {
      const char = maskFormat[i];
      const maskType = getMaskPlaceholderType(char);

      if (maskType) {
        // Find next matching character for this placeholder
        while (candidateIndex < candidates.length && !isMatchingMaskType(candidates[candidateIndex], maskType)) {
          candidateIndex += 1;
        }

        if (candidateIndex >= candidates.length) {
          // No more input, show placeholder
          displayValue += char;
        } else {
          // Use the matching character
          displayValue += transformByMaskType(candidates[candidateIndex], maskType);
          candidateIndex += 1;
        }
      } else {
        // Literal character
        displayValue += char;
      }
    }

    return displayValue;
  }, [getMaskPlaceholderType, isMatchingMaskType, transformByMaskType]);

  const getCursorPosition = useCallback((cleanValue, maskFormat) => {
    if (!maskFormat) {
      return 0;
    }

    // Extract clean value if it's an object
    const actualCleanValue = typeof cleanValue === 'object' && cleanValue !== null ? cleanValue.clean : cleanValue;
    const candidates = (typeof actualCleanValue === 'string' ? actualCleanValue : '').split('');

    let cursorPos = 0;
    let candidateIndex = 0;
    for (let i = 0; i < maskFormat.length; i += 1) {
      const char = maskFormat[i];
      const maskType = getMaskPlaceholderType(char);

      if (maskType) {
        while (candidateIndex < candidates.length && !isMatchingMaskType(candidates[candidateIndex], maskType)) {
          candidateIndex += 1;
        }

        if (candidateIndex >= candidates.length) {
          break;
        }

        cursorPos += 1;
        candidateIndex += 1;
      } else {
        cursorPos += 1;
      }
    }

    return cursorPos;
  }, [getMaskPlaceholderType, isMatchingMaskType]);

  const normalizeMaskedValue = useCallback((rawValue, maskFormat) => {
    if (!maskFormat) {
      return String(rawValue || '');
    }

    const raw = String(rawValue || '');
    let normalized = '';

    for (let i = 0; i < maskFormat.length; i += 1) {
      const maskChar = maskFormat[i];
      const maskType = getMaskPlaceholderType(maskChar);
      const rawChar = raw[i];

      if (!maskType) {
        normalized += maskChar;
      } else if (rawChar && rawChar !== maskChar && isMatchingMaskType(rawChar, maskType)) {
        normalized += transformByMaskType(rawChar, maskType);
      } else {
        normalized += maskChar;
      }
    }

    return normalized;
  }, [getMaskPlaceholderType, isMatchingMaskType, transformByMaskType]);

  const extractCleanValueFromNormalizedMask = useCallback((maskedValue, maskFormat) => {
    if (!maskFormat) {
      return String(maskedValue || '');
    }

    const masked = String(maskedValue || '');
    let clean = '';

    for (let i = 0; i < maskFormat.length && i < masked.length; i += 1) {
      const maskChar = maskFormat[i];
      const maskType = getMaskPlaceholderType(maskChar);
      const valueChar = masked[i];

      if (maskType && valueChar !== maskChar && isMatchingMaskType(valueChar, maskType)) {
        clean += transformByMaskType(valueChar, maskType);
      }
    }

    return clean;
  }, [getMaskPlaceholderType, isMatchingMaskType, transformByMaskType]);

  const updateMaskedValueByInputAction = useCallback((previousMaskedValue, maskFormat, inputType, inputData, rawCursorPos, rawValue) => {
    if (!maskFormat) {
      return String(rawValue || '');
    }

    const previousMasked = String(previousMaskedValue || maskFormat).padEnd(maskFormat.length, '');
    const next = previousMasked.split('');

    const findPreviousPlaceholderIndex = (startIndex) => {
      for (let i = startIndex; i >= 0; i -= 1) {
        if (getMaskPlaceholderType(maskFormat[i])) {
          return i;
        }
      }
      return -1;
    };

    const findNextPlaceholderIndex = (startIndex) => {
      for (let i = startIndex; i < maskFormat.length; i += 1) {
        if (getMaskPlaceholderType(maskFormat[i])) {
          return i;
        }
      }
      return -1;
    };

    if (inputType === 'insertText' && typeof inputData === 'string' && inputData.length === 1) {
      const baseIndex = Math.max(0, (rawCursorPos || 0) - 1);
      const targetIndex = findPreviousPlaceholderIndex(baseIndex);

      if (targetIndex >= 0) {
        const type = getMaskPlaceholderType(maskFormat[targetIndex]);
        if (isMatchingMaskType(inputData, type)) {
          next[targetIndex] = transformByMaskType(inputData, type);
        }
      }
      return next.join('');
    }

    if (inputType === 'deleteContentBackward') {
      const cursorIndex = Math.max(0, rawCursorPos || 0);
      const immediateLeftIndex = cursorIndex - 1;
      const targetIndex = immediateLeftIndex >= 0 && getMaskPlaceholderType(maskFormat[immediateLeftIndex])
        ? immediateLeftIndex
        : -1;
      if (targetIndex >= 0) {
        next[targetIndex] = maskFormat[targetIndex];
      }
      return next.join('');
    }

    if (inputType === 'deleteContentForward') {
      const targetIndex = findNextPlaceholderIndex(Math.max(0, rawCursorPos || 0));
      if (targetIndex >= 0) {
        next[targetIndex] = maskFormat[targetIndex];
      }
      return next.join('');
    }

    return normalizeMaskedValue(rawValue, maskFormat);
  }, [getMaskPlaceholderType, isMatchingMaskType, normalizeMaskedValue, transformByMaskType]);

  const setFieldValueWithMask = useCallback((field, rawValue, maskFormat, rawCursorPos, inputType, inputData, previousMaskedValue) => {
    if (!maskFormat) {
      dispatchValues(field, rawValue);
      return;
    }

    const maskedValue = updateMaskedValueByInputAction(previousMaskedValue, maskFormat, inputType, inputData, rawCursorPos, rawValue);
    const cleanValue = extractCleanValueFromNormalizedMask(maskedValue, maskFormat);

    if (typeof rawCursorPos === 'number') {
      const maxCursor = maskFormat ? maskFormat.length : String(rawValue || '').length;
      pendingCursorRef.current = Math.max(0, Math.min(rawCursorPos, maxCursor));
    }

    // Dispatch both clean and masked values
    dispatchValues(field, { clean: cleanValue, masked: maskedValue });
  }, [
    dispatchValues,
    extractCleanValueFromNormalizedMask,
    updateMaskedValueByInputAction,
  ]);

  const formatRegex = useMemo(() => getFormatRegex(format), [getFormatRegex, format]);
  const currentValue = fields[Field] || '';
  const cleanValueStr = typeof currentValue === 'object' && currentValue !== null ? currentValue.clean : currentValue;
  const displayValue = useMemo(() => buildDisplayValue(currentValue, format), [buildDisplayValue, currentValue, format]);
  const hasInvalidFormat = !!(cleanValueStr && !isLocationIdentifierValid(displayValue, format, formatRegex));

  useEffect(() => {
    if (previousFormatRef.current !== format) {
      dispatchValues(Field, '');
      previousFormatRef.current = format;
    }
  }, [Field, dispatchValues, format]);

  useEffect(() => {
    if (!setValidationError) {
      return;
    }

    const cleanStr = typeof currentValue === 'object' && currentValue !== null ? currentValue.clean : currentValue;
    const hasCurrentValueInvalidFormat = !!(cleanStr && !isLocationIdentifierValid(displayValue, format, formatRegex));

    setValidationError(hasCurrentValueInvalidFormat);
  }, [Field, currentValue, setValidationError, formatRegex, displayValue, format, isLocationIdentifierValid]);

  // Set cursor position after render
  useEffect(() => {
    if (inputRef.current) {
      const cursorPos = typeof pendingCursorRef.current === 'number'
        ? pendingCursorRef.current
        : getCursorPosition(currentValue, format);
      inputRef.current.setSelectionRange(cursorPos, cursorPos);
      pendingCursorRef.current = null;
    }
  }, [displayValue, format, getCursorPosition, currentValue]);

  return (
    <TextField
      fullWidth
      label={i18nLabel || Text}
      name={Field}
      size="small"
      placeholder={format}
      required={false}
      disabled={!(fields[DependentOn]?.id)}
      value={displayValue}
      error={hasInvalidFormat}
      onChange={(e) => setFieldValueWithMask(
        Field,
        e.target.value,
        format,
        e.target.selectionStart,
        e.nativeEvent?.inputType,
        e.nativeEvent?.data,
        displayValue,
      )}
      inputRef={inputRef}
    />
  );
};
const RemoteDataAutoCompleteElement = ({ config, fields, dispatchValues, setValidationError, cascadedOptions }) => {
  const { Field, Text, i18nLabel, SearchType, ServerFilter, Multiple, Required, ExtraFilterKeys = [], FreeSolo, LimitTags } = config || {};
  const { assignedContracts, assignedDivisions, assignedSourceSystems } = useAuth();

  const preloadedOptions = cascadedOptions?.[Field];

  useEffect(() => {
    if (Required && setValidationError) {
      if (Multiple) {
        if (!fields[Field]?.length) {
          setValidationError(true);
        } else {
          setValidationError(false);
        }
      } else if (!fields[Field]) {
        setValidationError(true);
      } else {
        setValidationError(false);
      }
    }
  }, [Field, Multiple, Required, fields, setValidationError]);

  const extraFilter = useMemo(() => {
    const _extraFilter = {};
    if (ExtraFilterKeys?.length) {
      ExtraFilterKeys?.forEach(({ PropName, ValuePath, FilterKey }) => {
        if (!PropName) {
          return;
        }

        const fieldPropObj = fields[PropName];
        let fieldValue = fieldPropObj;
        if (ValuePath) {
          if (fieldPropObj) {
            if (Array.isArray(fieldPropObj)) {
              fieldValue = fieldPropObj.map((item) => item[ValuePath]).filter((val) => val != null);
            } else {
              fieldValue = fieldPropObj[ValuePath];
            }
          } else {
            fieldValue = null;
          }
        }
        const extraFilterKey = FilterKey ?? PropName;
        _extraFilter[extraFilterKey] = fieldValue;
        if (equalsIgnoringCase(PropName, 'contractIDs')) {
          _extraFilter[extraFilterKey] = assignedContracts;
        }
        if (equalsIgnoringCase(PropName, 'divisionIds')) {
          _extraFilter[extraFilterKey] = assignedDivisions;
        }
        if (equalsIgnoringCase(PropName, 'clientIDs')) {
          _extraFilter['sourceSystemIds'] = assignedSourceSystems;
        }
      });
    }
    return _extraFilter;
  }, [ExtraFilterKeys, assignedContracts, assignedDivisions, assignedSourceSystems, fields]);

  return (
    <RemoteDataAutoComplete
      label={i18nLabel || Text}
      required={Required}
      placeholder="Select an option"
      name={Field}
      objectType={SearchType}
      fetcher={autoCompleteDataFetcher}
      selectedItem={fields[Field] ?? (Multiple ? [] : null)}
      onSelectedItemChanged={(val) => dispatchValues(Field, val)}
      serverFilter={ServerFilter}
      multiple={!!Multiple}
      extraFilter={extraFilter}
      freeSolo={FreeSolo}
      limitTags={LimitTags}
      optionsList={preloadedOptions?.length ? preloadedOptions : undefined}
    />
  );
};

const FixedOptionAutoCompleteElement = ({ config, fields, dispatchValues }) => {
  const { Field, Text, i18nLabel, Multiple, Options, Required } = config || {};
  return (
    <FixedOptionsAutoComplete
      multiple={Multiple}
      label={i18nLabel || Text}
      placeholder="Select an option"
      name={Field}
      required={Required}
      options={Options}
      selectedItem={fields[Field]}
      onSelectedItemChanged={(val) => dispatchValues(Field, val)}
    />
  );
};

const ClientAutoCompleteElement = ({ config, fields, dispatchValues }) => {
  const { Field, Text, i18nLabel, Multiple, Required, RenderOptionDecorator } = config || {};
  return (
    <ClientAutoComplete
      label={i18nLabel || Text}
      renderOptionDecorator={RenderOptionDecorator}
      multiple={Multiple}
      name="client"
      selectedItem={fields[Field] ?? (Multiple ? [] : null)}
      onValueChange={(val) => dispatchValues(Field, val)}
      required={Required}
    />
  );
};
const PetrohubClientAutoCompleteElement = ({ config, fields, dispatchValues }) => {
  const { Field, Text, i18nLabel, Multiple, Required } = config || {};
  return (
    <PetrohubClientAutoComplete
      label={i18nLabel || Text}
      multiple={Multiple}
      name="client"
      selectedItem={fields[Field] ?? (Multiple ? [] : null)}
      onValueChange={(val) => dispatchValues(Field, val)}
      required={Required}
      renderOptionDecorator
    />
  );
};
const ReportAddressAutoComplateElement = ({ config, fields, dispatchValues }) => {
  const { assignedSourceSystems } = useAuth();
  const userPage = getUserCurrentPageName();
  const dispatch = useDispatch();

  const { Field, Text, i18nLabel, Multiple, Required, ExtraFilterKeys = [] } = config || {};
  const extraFilter = useMemo(() => {
    const filter = getExtraFilter(ExtraFilterKeys, fields);
    // Include clientIDs from scenario selection
    if (fields['clientIDs'] && fields['clientIDs'].length > 0) {
      filter['clientIDs'] = fields['clientIDs'].map((item) => item.id ?? item);
    }
    return filter;
  }, [ExtraFilterKeys, fields]);

  const dispatchFilterValuesChanged = useCallback(
    (propName, propValue) => {
      const mappedValues = propValue.map((item) => ({ id: item?.ClientID, text: item?.ClientName }));
      dispatch(
        actionMap[userPage].updateFilterValue({
          propName,
          propValue: mappedValues,
        })
      );
    },
    [dispatch, userPage]
  );
  const handleReportAddressAutoComplete = useCallback(async (val) => {
    dispatchValues(Field, val);
    try {
      if (val && val.length > 0) {
        const hasClientSelected = fields['clientIDs'] && fields['clientIDs'].length > 0;
        if (!hasClientSelected) {
          const response = await ClientApi.fetchAllClientsFuel({ reportAddressIDs: val.map((item) => item.id).join(','), pageSize: 20, sourceSystemIds: assignedSourceSystems, functionality: PageName[userPage] });
          if (response) {
            dispatchFilterValuesChanged('clientIDs', response?.Data);
          }
        }
      } else {
        dispatchFilterValuesChanged('clientIDs', []);
      }
    } catch (error) {
      console.error(error);
    }
  }, [Field, assignedSourceSystems, dispatchFilterValuesChanged, dispatchValues, fields, userPage]);
  return (
    <ReportAddressAutoComplate
      label={i18nLabel || Text}
      multiple={Multiple}
      name={Field}
      selectedItem={fields[Field] ?? (Multiple ? [] : null)}
      onValueChange={handleReportAddressAutoComplete}
      required={Required}
      renderOptionDecorator
      extraFilter={extraFilter}
    />
  );
};
const ScenarioAutocomplateElement = ({ config, fields, dispatchValues }) => {
  const { assignedSourceSystems } = useAuth();
  const userPage = getUserCurrentPageName();
  const dispatch = useDispatch();
  const { Field, Text, i18nLabel, Multiple, Required, ExtraFilterKeys = [] } = config || {};
  const extraFilter = useMemo(() => {
    const filter = getExtraFilter(ExtraFilterKeys, fields);
    // Include clientIDs from report address selection
    if (fields['clientIDs'] && fields['clientIDs'].length > 0) {
      filter['clientIDs'] = fields['clientIDs'].map((item) => item.id ?? item);
    }
    return filter;
  }, [ExtraFilterKeys, fields]);

  const dispatchFilterValuesChanged = useCallback(
    (propName, propValue) => {
      dispatch(
        actionMap[userPage].updateFilterValue({
          propName,
          propValue,
        })
      );
    },
    [dispatch, userPage]
  );
  const handleScenarioAutoComplete = useCallback(async (val) => {
    dispatchValues(Field, val);
    try {
      if (val && val.length > 0) {
        const hasClientSelected = fields['clientIDs'] && fields['clientIDs'].length > 0;
        if (!hasClientSelected) {
          const response = await ClientApi.fetchAllClientsFuel({
            scenarioIDs: val.map((item) => item.id).join(','),
            pageSize: 20,
            sourceSystemIds: assignedSourceSystems,
            functionality: PageName[userPage],
          });
          if (response) {
            dispatchFilterValuesChanged('clientIDs', response?.Data?.map((item) => ({ id: item?.ClientID, text: item?.ClientName })));
          }
        }
      } else {
        dispatchFilterValuesChanged('clientIDs', []);
      }
    } catch (error) {
      console.error(error);
    }
  }, [Field, assignedSourceSystems, dispatchFilterValuesChanged, dispatchValues, fields, userPage]);
  return (
    <ScenarioAutocomplate
      label={i18nLabel || Text}
      multiple={Multiple}
      name={Field}
      selectedItem={fields[Field] ?? (Multiple ? [] : null)}
      onValueChange={handleScenarioAutoComplete}
      required={Required}
      renderOptionDecorator
      extraFilter={extraFilter}
    />
  );
};
const StatusSelectElement = ({ config, fields, dispatchValues }) => {
  const { Field, Text, i18nLabel, Multiple, Required } = config || {};
  return (
    <StatusSelect
      label={i18nLabel || Text}
      selectedValue={fields[Field]}
      onValueChange={(val) =>
        dispatchValues(Field, val)}
      required={Required}
      Multiple={Multiple}
    />
  );
};

const LocationAutoCompleteElement = ({ config, fields, dispatchValues }) => {
  const { Field, Text, i18nLabel, Multiple, RenderOptionDecorator } = config || {};
  return (
    <LocationAutoComplete
      required
      renderOptionDecorator={RenderOptionDecorator}
      label={i18nLabel || Text}
      multiple={Multiple}
      name="location"
      selectedItem={fields[Field] || null}
      onValueChange={(val) => dispatchValues(Field, val)}
    />
  );
};

const ProjectAutoCompleteElement = ({ config, fields, dispatchValues }) => {
  const { Field, Text, i18nLabel, Multiple } = config || {};
  return (
    <ProjectAutoComplete
      label={i18nLabel || Text}
      multiple={Multiple}
      name="project"
      selectedItem={fields[Field] || null}
      onValueChange={(val) => dispatchValues(Field, val)}
    />
  );
};

const SwitchElement = ({ config, fields, dispatchValues }) => {
  const { Field, Text, i18nLabel } = config || {};
  return (
    <FormGroup>
      <FormControlLabel
        control={
          <Switch
            checked={!!fields[Field]}
            size="small"
            onChange={(e) => dispatchValues(Field, e.target.checked)}
          />
        }
        label={i18nLabel || Text}
        name={Field}
        // sx={{ pt: 1 }}
      />
    </FormGroup>
  );
};

const DropdownElement = ({ config, fields, dispatchValues }) => {
  const { Field, Text, i18nLabel, Options = [], Order, DefaultValue = '', Required } = config || {};
  return (
    <FormControl fullWidth size="small">
      <InputLabel id={`${Field}_${Order}`}>{i18nLabel || Text}</InputLabel>
      <Select
        labelId={`${Field}_${Order}`}
        value={fields[Field]}
        defaultValue={DefaultValue}
        label={i18nLabel || Text}
        name={Field}
        required={Required}
        onChange={(e) => dispatchValues(Field, e.target.value)}
      >
        {
          Options?.map(({ id, text }) => <MenuItem key={id} value={id}>{text}</MenuItem>)
        }
      </Select>
    </FormControl>
  );
};

const DatepickerElement = ({ config, fields, dispatchValues }) => {
  const { Field, Text, i18nLabel } = config || {};
  const { dateFormat } = useAuth();

  return (
    <DatePicker
      inputFormat={dateFormat}
      label={i18nLabel || Text}
      value={fields[Field] || null}
      renderInput={(params) => <TextField size="small" fullWidth {...params} />}
      name={Field}
      disableMaskedInput
      onChange={(date) => {
        const isDate = (date?.getTime() > 0);
        dispatchValues(Field, isDate ? date?.toISOString() : '');
      }}
    />
  );
};

export const SearchFilterItemFactory = ({ config, fields, dispatchValues, setValidationError, cascadedOptions }) => {
  const { t } = useTranslation();

  const clone = window.structuredClone(config);
  const { Field, ControlType = 'TextField' } = clone || {};
  clone['i18nLabel'] = t(`Label_${Field}`);
  const comp = {
    TextField: (
      <TextFieldElement
        config={clone}
        fields={fields}
        setValidationError={setValidationError}
        dispatchValues={dispatchValues}
      />
    ),
    MaskTextField: (
      <MaskTextFieldElement
        config={clone}
        fields={fields}
        setValidationError={setValidationError}
        dispatchValues={dispatchValues}
      />
    ),
    Switch: (
      <SwitchElement
        config={clone}
        fields={fields}
        setValidationError={setValidationError}
        dispatchValues={dispatchValues}
      />
    ),
    RemoteDataAutoComplete: (
      <RemoteDataAutoCompleteElement
        config={clone}
        fields={fields}
        setValidationError={setValidationError}
        dispatchValues={dispatchValues}
        cascadedOptions={cascadedOptions}
      />
    ),
    Dropdown: (
      <DropdownElement
        config={clone}
        fields={fields}
        setValidationError={setValidationError}
        dispatchValues={dispatchValues}
      />
    ),
    FixedOptionAutoComplete: (
      <FixedOptionAutoCompleteElement
        config={clone}
        fields={fields}
        setValidationError={setValidationError}
        dispatchValues={dispatchValues}
      />
    ),
    DatePicker: (
      <DatepickerElement
        config={clone}
        fields={fields}
        setValidationError={setValidationError}
        dispatchValues={dispatchValues}
      />
    ),
    ClientAutoComplete: (
      <ClientAutoCompleteElement
        config={clone}
        fields={fields}
        setValidationError={setValidationError}
        dispatchValues={dispatchValues}
      />
    ),
    PetrohubClientAutoComplete: (
      <PetrohubClientAutoCompleteElement
        config={clone}
        fields={fields}
        setValidationError={setValidationError}
        dispatchValues={dispatchValues}
      />
    ),
    ReportAddressAutoComplate: (
      <ReportAddressAutoComplateElement
        config={clone}
        fields={fields}
        setValidationError={setValidationError}
        dispatchValues={dispatchValues}
      />
    ),
    ScenarioAutocomplate: (
      <ScenarioAutocomplateElement
        config={clone}
        fields={fields}
        setValidationError={setValidationError}
        dispatchValues={dispatchValues}
      />
    ),
    LocationAutoComplete: (
      <LocationAutoCompleteElement
        config={clone}
        fields={fields}
        setValidationError={setValidationError}
        dispatchValues={dispatchValues}
      />
    ),
    ProjectAutoComplete: (
      <ProjectAutoCompleteElement
        config={clone}
        fields={fields}
        setValidationError={setValidationError}
        dispatchValues={dispatchValues}
      />
    ),

    StatusSelect: (
      <StatusSelectElement
        config={clone}
        fields={fields}
        setValidationError={setValidationError}
        dispatchValues={dispatchValues}
      />
    )
  };

  return comp[ControlType];
};
